/**
 * ABOUTME: Main SmartQuote component that orchestrates the entire quote request flow
 * ABOUTME: Manages state and transitions between selection, sending, and success views
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Tradesperson } from '@/lib/types';
import { useTradeQuoteRequest } from '@/hooks/useTradeQuoteRequest';
import { debug } from '@/lib/utils';
import { toast } from 'sonner';
import { generateMessageTemplate, hasPlaceholders } from './smartQuoteUtils';
import { SmartQuoteSendView } from './SmartQuoteSendView';
import { SmartQuoteSuccessView } from './SmartQuoteSuccessView';
import { UnifiedSmartQuoteAI } from './SmartQuoteAI/UnifiedSmartQuoteAI';
import { getTradeDisplayName } from '@/lib/tradeCategories';
import { useProfile } from '@/hooks/useProfile';

interface SmartQuoteProps {
  initialValue?: string;
  postcode?: string;
  tradespeople: Tradesperson[];
  isLoading?: boolean;
  onComplete?: () => void;
  onStateChange?: (state: 'unified-ai' | 'send' | 'success') => void;
  onActionButtonChange?: (show: boolean) => void;
}

export function SmartQuote({
  initialValue = '',
  postcode,
  tradespeople,
  onStateChange,
  onActionButtonChange
  // isLoading and onComplete accepted by interface but handled by SmartQuoteWrapper
}: SmartQuoteProps) {
  // State for tracking which section is visible
  const [uiState, setUiState] = useState<'unified-ai' | 'send' | 'success'>('unified-ai');

  // AI-generated content state
  const [aiData, setAiData] = useState<{
    message: string;
    selectedTradespeople: Tradesperson[];
    detectedCategory?: string;
    confidence: number;
  } | null>(null);

  // Notify parent component when state changes
  useEffect(() => {
    if (onStateChange) {
      onStateChange(uiState);
    }
  }, [uiState, onStateChange]);

  // Input state
  const [userPostcode, setUserPostcode] = useState(postcode || '');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  // Message and contact state
  const [fullMessage, setFullMessage] = useState('');
  const [name, setName] = useState('');

  // Send tracking state - moved from SmartQuoteSendView to persist across app switches
  const [sendingStatus, setSendingStatus] = useState<Record<string, 'pending' | 'sending' | 'sent' | 'error'>>({});
  const [sendMethod, setSendMethod] = useState<Record<string, 'email' | 'messages' | null>>({});

  // Pre-fill name and postcode from user profile
  const { profile } = useProfile();

  useEffect(() => {
    if (profile) {
      if (!name && profile.username) setName(profile.username);
      if (!userPostcode && profile.postcode) setUserPostcode(profile.postcode);
    }
  }, [profile]); // eslint-disable-line react-hooks/exhaustive-deps

  // Trade selection hooks from useTradeQuoteRequest
  const {
    handleTradeSelection,
    submitQuoteRequests,
    selectedTradespeople,
    resetSelection,
    setMessage,
  } = useTradeQuoteRequest(undefined, true);

  // Generate a template message
  useEffect(() => {
    // Get the display name for the selected category
    const jobType = selectedCategory ? getTradeDisplayName(selectedCategory) : undefined;
    // Generate message without email/phone - users will add contact info in their email client
    const template = generateMessageTemplate(initialValue, userPostcode, name, jobType);
    setFullMessage(template);
    setMessage(template);
  }, [initialValue, userPostcode, name, selectedCategory, setMessage]);

  // Check if message has unfilled placeholders or template values
  const validateMessageComplete = useCallback((message: string): boolean => {
    if (hasPlaceholders(message)) {
      toast.error('Please fill in all required information before sending');
      return false;
    }

    const templatePlaceholders = [
      'YOUR_POSTCODE', 'TYPE_OF_WORK', 'PLEASE_DESCRIBE_YOUR_JOB', 'YOUR_NAME',
      '[Please enter your postcode]', '[Please select a trade type]',
      '[Please describe what you need help with]', '[Please enter your name]'
    ];
    if (templatePlaceholders.some(p => message.includes(p))) {
      toast.error('Please complete your profile and job details before sending');
      return false;
    }

    return true;
  }, []);

  // Record a quote request via email or SMS
  const recordQuoteSent = useCallback(async (tradeId: string, method: 'email' | 'phone') => {
    try {
      if (!validateMessageComplete(fullMessage)) return false;

      const success = await submitQuoteRequests(method, '', [tradeId]);

      if (success) {
        debug.log(`${method} sending action recorded for trade:`, tradeId);
        return true;
      } else {
        const label = method === 'email' ? 'quote request' : 'text message request';
        toast.error(`Failed to record ${label}. Please try again.`);
        return false;
      }
    } catch (error) {
      const label = method === 'email' ? 'email' : 'SMS';
      debug.error(`Error recording ${label} submission:`, error);
      toast.error(`An error occurred while recording your ${label} request. Please try again.`);
      return false;
    }
  }, [fullMessage, submitQuoteRequests, validateMessageComplete]);

  // Handle unified AI completion and go straight to sending
  const handleUnifiedAIComplete = useCallback((data: {
    message: string;
    selectedTradespeople: Tradesperson[];
    detectedCategory?: string;
    confidence: number;
  }) => {
    setAiData(data);
    setFullMessage(data.message);
    setMessage(data.message);

    // Set the detected category if we found one
    if (data.detectedCategory) {
      setSelectedCategory(data.detectedCategory);
    }

    // Set selected tradespeople in the hook
    data.selectedTradespeople.forEach(trade => {
      handleTradeSelection(trade);
    });

    setUiState('send');
  }, [setMessage, handleTradeSelection]);

  // Reset the form and send tracking state
  const resetForm = useCallback(() => {
    setSelectedCategory(null);
    setFullMessage('');
    setName('');
    setSendingStatus({});
    setSendMethod({});
    setAiData(null);
    resetSelection();
    setUiState('unified-ai');
  }, [resetSelection]);

  return (
    <div className="w-full">
      {/* Unified AI View */}
      {uiState === 'unified-ai' && (
        <UnifiedSmartQuoteAI
          tradespeople={tradespeople}
          userName={name}
          userPostcode={userPostcode}
          onComplete={handleUnifiedAIComplete}
        />
      )}

      {/* Send View - AI already chose tradespeople and message, so go straight to sending */}
      {uiState === 'send' && aiData && (
        <SmartQuoteSendView
          message={fullMessage}
          selectedTradespeople={selectedTradespeople}
          tradespeople={tradespeople}
          initialName={name}
          sendingStatus={sendingStatus}
          setSendingStatus={setSendingStatus}
          sendMethod={sendMethod}
          setSendMethod={setSendMethod}
          onRecordEmail={(tradeId) => recordQuoteSent(tradeId, 'email')}
          onRecordSMS={(tradeId) => recordQuoteSent(tradeId, 'phone')}
          onMessageChange={(msg) => {
            setFullMessage(msg);
            setMessage(msg);
          }}
          onComplete={() => setUiState('success')}
          onBack={() => setUiState('unified-ai')}
          onActionButtonChange={onActionButtonChange}
        />
      )}

      {/* Success View */}
      {uiState === 'success' && (
        <SmartQuoteSuccessView
          selectedTradespeople={selectedTradespeople}
          resetForm={resetForm}
        />
      )}
    </div>
  );
}
